import { useEffect, useState } from "react";
import { Mail, Loader2, CheckCircle2, AlertTriangle, Paperclip, RefreshCw } from "lucide-react";
import api from "@/lib/api";

const fmt = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleString("es-ES", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
};

const DELIVERY_LABEL = {
  delivered: "Entregado",
  sent: "Aceptado por el servidor",
  bounced: "Rebotado (dirección errónea o buzón lleno)",
  complained: "Marcado como spam por el destinatario",
  delivery_delayed: "Entrega retrasada, reintentando",
  no_access: "Enviado · estado no consultable",
  unknown: "Enviado",
};

/** Registro de informes enviados al personal, para comprobar si salieron. */
export const CoachingEmailsLog = ({ refreshKey = 0 }) => {
  const [items, setItems] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/employees/coaching-emails", { params: { limit: 30 } });
      setItems(data.items || []);
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshKey]);

  if (items === null) return null;

  return (
    <section className="bg-white border border-slate-200 rounded-lg p-5" data-testid="coaching-emails-log">
      <div className="flex items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-2">
          <Mail className="h-4 w-4 text-slate-400" strokeWidth={1.6} />
          <h3 className="font-display text-base md:text-lg font-bold text-slate-900">Informes enviados al personal</h3>
        </div>
        <button
          onClick={load}
          disabled={loading}
          className="text-xs font-semibold text-slate-500 hover:text-slate-900 inline-flex items-center gap-1.5 transition-colors"
          data-testid="coaching-log-refresh"
        >
          <RefreshCw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} /> Actualizar
        </button>
      </div>

      {items.length === 0 ? (
        <p className="text-sm text-slate-500">
          Todavía no has enviado ningún informe. Usa el botón del sobre en la tabla de empleados.
        </p>
      ) : (
        <div className="space-y-2">
          {items.map((it) => {
            const failed = it.status === "error" || it.delivery === "bounced";
            return (
              <div
                key={it.id}
                className="flex flex-wrap items-center justify-between gap-2 py-2.5 px-3 rounded-lg border border-slate-100 bg-slate-50/60"
                data-testid={`coaching-log-row-${it.id}`}
              >
                <div className="min-w-0">
                  <div className="text-sm font-semibold text-slate-900 truncate">
                    {it.employee_name} <span className="font-normal text-slate-500">· {it.to}</span>
                  </div>
                  <div className="text-xs text-slate-500 flex items-center gap-2 mt-0.5">
                    {fmt(it.sent_at)}
                    {it.attachment && (
                      <span className="inline-flex items-center gap-1">
                        <Paperclip className="h-3 w-3" /> {it.attachment}
                      </span>
                    )}
                  </div>
                </div>
                <div className={`text-xs font-semibold inline-flex items-center gap-1.5 ${failed ? "text-rose-600" : "text-emerald-700"}`}>
                  {failed ? <AlertTriangle className="h-3.5 w-3.5" /> : <CheckCircle2 className="h-3.5 w-3.5" />}
                  {it.status === "error"
                    ? `No salió: ${it.error || "error de envío"}`
                    : DELIVERY_LABEL[it.delivery] || "Enviado"}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
};
